function createOptions(options) {
  if (!options) {
    return [];
  }

  return options.map(option => ({ label: option.beschrijving, id: option.code }))
}

function getRequirementsOfType(requirements, requirementType) {
  return requirements.filter(({ type }) => type === requirementType);
}

function filterFilterOptions(allVacancies, filterOptions) {
  const options = {
    arbeidsregimes: new Set(),
    arbeidsstelsels: new Set(),
    contract: new Set(),
    diplomaniveau: new Set(),
    diplomas: new Set(),
    jobdomeinen: new Set(),
    rijbewijzen: new Set(),
    sjablonen: new Set(),
    talen: new Set(),
  };

  if (!allVacancies) {
    return options.reduce((acc, curr) => ({ ...acc, [curr]: [] }), {});
  }

  for (let i = 0; i < allVacancies.length; i += 1) {
    const { vacancies } = allVacancies[i].properties;

    for (let j = 0; j < vacancies.length; j += 1) {
      const vacancy = vacancies[j];

      const {
        functie,
        arbeidsovereenkomst,
        profiel: { vereisten },
      } = vacancy.properties;

      if (functie.jobdomeinen) {
        functie.jobdomeinen.forEach(jobDomain => options.jobdomeinen.add(jobDomain.code));
      }

      if (functie.beroepsprofiel) {
        options.sjablonen.add(functie.beroepsprofiel.code);
      }

      if (arbeidsovereenkomst.arbeidsRegime && options?.arbeidsregimes) {
        options.arbeidsregimes.add(arbeidsovereenkomst.arbeidsRegime.code);
      }

      if (arbeidsovereenkomst.arbeidsStelsel) {
        arbeidsovereenkomst.arbeidsStelsel.forEach(stelsel => options.arbeidsstelsels.add(stelsel.code));
      }

      if (arbeidsovereenkomst.arbeidsContractDuur) {
        options.contract.add(arbeidsovereenkomst.arbeidsContractDuur);
      }

      getRequirementsOfType(vereisten, 'talen').forEach(({ code }) => options.talen.add(code));
      getRequirementsOfType(vereisten, 'studie').forEach(({ diplomaNiveau, code }) => {
        options.diplomas.add(code);
        options.diplomaniveau.add(diplomaNiveau.code);
      });
      getRequirementsOfType(vereisten, 'rijbewijs').forEach(({ code }) => options.rijbewijzen.add(code));
    }
  }

  Object.keys(options).forEach((key) => {
    options[key] = Array.from(options[key])
      .map(option => filterOptions[key].find(filterOption => filterOption.code === option))
      .filter(option => !!option)
      .map(option => ({ label: option.beschrijving, id: option.code }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  return options;
}

function vacancyMatchesFilter(vacancy, filter, cutoffDate) {
  const {
    functie,
    arbeidsovereenkomst,
    profiel: { vereisten },
    publicatieDatum,
  } = vacancy.properties;

  if (cutoffDate && new Date(publicatieDatum) < cutoffDate) {
    return false;
  }

  if (filter.jobDomain.length && !functie.jobdomeinen?.find((jobDomain) => {
    return filter.jobDomain.map(jd => jd.id).includes(jobDomain.code);
  })) {
    return false;
  }

  if (filter.job.length && !filter.job.map(j => j.id).includes(functie.beroepsprofiel?.code)) {
    return false;
  }

  if (filter.workSchedule && arbeidsovereenkomst.arbeidsRegime?.code !== filter.workSchedule) {
    return false;
  }

  if (filter.timeArrangement.length && !arbeidsovereenkomst.arbeidsStelsel?.find(stelsel => filter.timeArrangement.map(ta => ta.id).includes(stelsel.code))) {
    return false;
  }

  if (filter.contractType && arbeidsovereenkomst.arbeidsContractDuur !== filter.contractType) {
    return false;
  }

  const educationRequirements = getRequirementsOfType(vereisten, 'studie');
  if (filter.educationLevel && !educationRequirements.find(requirement => requirement.diplomaNiveau.code === filter.educationLevel)) {
    return false;
  }

  if (filter.degree && !educationRequirements.find(requirement => requirement.code === filter.degree)) {
    return false;
  }

  const languageRequirements = getRequirementsOfType(vereisten, 'talen');
  if (filter.languages.length && languageRequirements.length) {
    if (!languageRequirements.find((requirement) => {
      const code = requirement?.score?.code;
      let validCodes = ['40', '41', '42', '43'];
      validCodes = validCodes.slice(0, validCodes.indexOf(code));

      return filter.languages.find((l) => {
        const level = l?.data?.level;
        return l.id === requirement.code && (!level || !validCodes.includes(level));
      })
    })) {
      return false;
    }
  }

  const driversLicenseRequirements = getRequirementsOfType(vereisten, 'rijbewijs');
  if (filter.driverLicense.length && driversLicenseRequirements.length) {
    if (!driversLicenseRequirements.find((requirement) => {
      return filter.driverLicense.find(dl => requirement.code.startsWith(dl.id));
    })) {
      return false;
    }
  }

  return true;
}

function companyMatchesFilter(company, filter) {
  const { mobilityOffer } = company.properties;

  if (filter.refundBusTrain && !(mobilityOffer?.pt_nmbs || mobilityOffer?.pt_delijn || mobilityOffer?.pt_tec || mobilityOffer?.pt_mivb)) {
    return false;
  }

  if (filter.organizationCollectiveTransport && !mobilityOffer?.pt_coll) {
    return false;
  }

  if (filter.organizationCarpool && !mobilityOffer?.carp) {
    return false;
  }

  if (filter.carSharingSubscription && !mobilityOffer?.car_sharing) {
    return false;
  }

  if (filter.electricCarChargingStation && !mobilityOffer?.car_charger) {
    return false;
  }

  if (filter.companyCar && !mobilityOffer?.car_salary) {
    return false;
  }

  if (filter.telecommutingIsPossible && !mobilityOffer?.tele) {
    return false;
  }

  if (filter.flexibleHours && !mobilityOffer?.flex) {
    return false;
  }

  if (filter.bicycleAllowance && !mobilityOffer?.bike_compen) {
    return false;
  }

  if (filter.companyOrLeaseBikes && !mobilityOffer?.bike_company) {
    return false;
  }

  if (filter.chargingStationEBikes && !mobilityOffer?.bike_charger) {
    return false;
  }

  if (filter.changingRoomAndOrShowers && !(mobilityOffer?.bike_shower || mobilityOffer?.bike_dressing)) {
    return false;
  }

  return true;
}

function getCutoffDate(dayLimit) {
  if (!dayLimit) {
    return undefined;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  today.setDate(today.getDate() - dayLimit);
  return today;
}

function filterVacancies(vacancyLocations, filter) {
  if (!vacancyLocations) {
    return [];
  }

  const filteredVacancies = [];
  const cutoffDate = getCutoffDate(filter.daysSinceReleaseLimit);

  for (let i = 0; i < vacancyLocations.length; i += 1) {
    const vacancyLocation = vacancyLocations[i];

    if (companyMatchesFilter(vacancyLocation, filter)) {
      const locationVacancies = vacancyLocation.properties.vacancies.filter((vacancy) => {
        return vacancyMatchesFilter(vacancy, filter, cutoffDate);
      });
      const normalCompanyVacancies = locationVacancies?.filter((v) => {
        return ['GEWONE_BEDRIJVEN'].includes(v?.properties?.leverancier?.type) || v?.properties?.nonKBOaddressMatch;
      });
      const jobAggregatorVacancies = locationVacancies?.filter((v) => {
        return ['DERDENLEVERANCIERS', 'INTERMEDIAIREN'].includes(v?.properties?.leverancier?.type) && !v?.properties?.nonKBOaddressMatch;
      });

      if (normalCompanyVacancies.length) {
        filteredVacancies.push({
          ...vacancyLocation,
          properties: {
            ...vacancyLocation.properties,
            vacancies: normalCompanyVacancies,
            vacanciesCount: normalCompanyVacancies.length,
            isJobAggregator: false,
            vacancyStringForCluserLogic: (
              `${normalCompanyVacancies.map(v => `${vacancyLocation?.properties?.OIDN}|${v?.properties?.vacatureReferentie?.vdabReferentie}`).join(',')},`
            ),
          },
        });
      }

      if (jobAggregatorVacancies.length) {
        filteredVacancies.push({
          ...vacancyLocation,
          properties: {
            ...vacancyLocation.properties,
            vacancies: jobAggregatorVacancies,
            vacanciesCount: jobAggregatorVacancies.length,
            isJobAggregator: true,
            vacancyStringForCluserLogic: (
              `${jobAggregatorVacancies.map(v => `${vacancyLocation?.properties?.OIDN}|${v?.properties?.vacatureReferentie?.vdabReferentie}`).join(',')},`
            ),
          },
        });
      }
    }
  }

  return filteredVacancies;
}

export {
  createOptions,
  filterFilterOptions,
  filterVacancies,
  getCutoffDate,
  vacancyMatchesFilter,
};